/**
 * Explore drawer slot-reel loading cards: per casino, plus a per-user opt-in.
 *
 * Driven only by `showLoadingReels` fields, same pattern as `showPOP` (lib/showPop.ts):
 * - casino object (Mongo `casinos`, passed through GetAllCasinos as-is and persisted as
 *   `casinoSchema`): turns the reels on for everyone in that casino;
 * - user record (Mongo `users`, returned by Login and GetMe and kept as
 *   `persistedState.userInfo.showLoadingReels`): turns the reels on for that user in
 *   every casino. Opt-in only: a user with 0 / missing never turns a casino's reels off.
 *
 * Values: 1, '1' or true -> on; 0, '0', false, missing, null or anything else -> off.
 * Reels show when the casino OR the user is on; otherwise the original gray pulsing
 * skeleton cards (the default).
 *
 * Display-only: it only picks which placeholder cards show while the insight data is
 * loading. The insight data path, API params, caches and responses are not touched.
 * No casino ids or users are hardcoded; setting the field is an admin / DB action.
 */
export type ShowLoadingReelsCasino = { showLoadingReels?: unknown } | null | undefined;
export type ShowLoadingReelsUser = { showLoadingReels?: unknown } | null | undefined;

export const isShowLoadingReelsValue = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') return value.trim() === '1';
  return false;
};

const hasShowLoadingReels = (record: ShowLoadingReelsCasino | ShowLoadingReelsUser): boolean =>
  !!record && typeof record === 'object' && isShowLoadingReelsValue(record.showLoadingReels);

/** The user's own opt-in (users.showLoadingReels), regardless of the casino. */
export const userOptedIntoLoadingReels = (user: ShowLoadingReelsUser): boolean =>
  hasShowLoadingReels(user);

/** Reels when the casino has them on, or the logged-in user opted in. */
export const shouldShowLoadingReels = (
  casino: ShowLoadingReelsCasino,
  user?: ShowLoadingReelsUser
): boolean => hasShowLoadingReels(casino) || hasShowLoadingReels(user);

/** Redux selector: current casino + logged-in user. */
export const selectShowLoadingReels = (state: {
  app: {
    persistedState: { casinoSchema?: ShowLoadingReelsCasino; userInfo?: ShowLoadingReelsUser };
  };
}): boolean =>
  shouldShowLoadingReels(state.app.persistedState.casinoSchema, state.app.persistedState.userInfo);
